export interface OnboardingProgress {
  id: string;
  user_id: string;
  current_step: number;
  completed_steps: number[];
  assessment_results?: AssessmentResults;
  language_goals?: LanguageGoals;
  selected_vocabulary: string[];
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface PracticeQuestion {
  id: string;
  type: "multiple_choice" | "fill_blank" | "translation" | "listening";
  question: string;
  options?: string[];
  correct_answer: string;
  explanation?: string;
  hint?: string;
  difficulty: number;
}

export interface AssessmentQuestion {
  id: string;
  language: string;
  difficulty_level: 1 | 2 | 3 | 4 | 5;
  question_type: "multiple_choice" | "fill_blank" | "translation" | "listening";
  question_text: string;
  options?: string[];
  correct_answer: string;
  grammar_point?: string;
  category?: string;
  explanation?: string;
  hint?: string;
  created_at: string;
}

export interface AssessmentAnswer {
  question_id: string;
  user_answer: string;
  is_correct: boolean;
  time_taken: number; // in seconds
  difficulty_level: number;
  grammar_point?: string;
}

export interface AssessmentResults {
  language: string;
  level_determined: LanguageLevel;
  score: number;
  total_questions: number;
  accuracy: number;
  areas_of_strength: string[];
  areas_for_improvement: string[];
  recommended_starting_point: RecommendedStartingPoint;
  question_responses: AssessmentAnswer[];
  time_taken: number; // total time in seconds
}

export interface SkillAssessment {
  id: string;
  user_id: string;
  language: string;
  assessment_type: 'initial' | 'progress' | 'placement';
  level_determined: LanguageLevel;
  score: number;
  total_questions: number;
  areas_of_strength: string[];
  areas_for_improvement: string[];
  recommended_starting_point: RecommendedStartingPoint;
  question_responses: AssessmentAnswer[];
  created_at: string;
}

export interface LanguageGoals {
  target_language: string;
  native_language: string;
  learning_purpose: LearningPurpose[];
  time_commitment: TimeCommitment;
  fluency_timeline: FluencyTimeline;
  specific_goals: string[];
  motivation_level: 1 | 2 | 3 | 4 | 5;
}

export interface RecommendedStartingPoint {
  suggested_level: LanguageLevel;
  recommended_topics: string[];
  focus_areas: string[];
  estimated_study_time: number; // hours per week
  next_steps: string[];
}

export interface VocabularyItem {
  word: string;
  translation: string;
  category: string;
  difficulty: number;
  frequency_rank: number;
  audio_url?: string;
  example_sentence?: string;
  known: boolean;
}

export interface OnboardingStep {
  id: number;
  title: string;
  description: string;
  component: string;
  required: boolean;
  estimated_time: number; // in minutes
  can_skip: boolean;
}

// Enums and Union Types
export type LanguageLevel =
  | 'absolute_beginner'
  | 'beginner_low'
  | 'beginner_high'
  | 'intermediate_low'
  | 'intermediate_high'
  | 'advanced_low'
  | 'advanced_high'
  | 'native';

export type LearningPurpose =
  | 'travel'
  | 'business'
  | 'academic'
  | 'personal_interest'
  | 'professional_development'
  | 'family_heritage'
  | 'relocation'
  | 'cultural_exploration';

export type TimeCommitment =
  | '15_min_daily'
  | '30_min_daily'
  | '1_hour_daily'
  | '2_hours_daily'
  | 'weekend_only'
  | 'flexible';

export type FluencyTimeline =
  | '3_months'
  | '6_months'
  | '1_year'
  | '2_years'
  | 'no_rush';

export type OnboardingStepType =
  | 'welcome'
  | 'language_goals'
  | 'skill_assessment'
  | 'vocabulary_builder'
  | 'feature_tour'
  | 'guided_practice'
  | 'completion';

// Constants
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 1,
    title: 'Welcome to Hans Card',
    description: 'Get started with your language learning journey',
    component: 'Welcome',
    required: true,
    estimated_time: 2,
    can_skip: false
  },
  {
    id: 2,
    title: 'Language Goals',
    description: 'Tell us about your language learning objectives',
    component: 'LanguageGoals',
    required: true,
    estimated_time: 5,
    can_skip: false
  },
  {
    id: 3,
    title: 'AI Vocabulary Assessment',
    description: 'Chat with our AI to build your personalized vocabulary',
    component: 'VocabularyBuilder',
    required: true,
    estimated_time: 10,
    can_skip: false
  },
  {
    id: 4,
    title: 'Feature Tour',
    description: 'Discover what Hans Card can do for you',
    component: 'FeatureTour',
    required: true,
    estimated_time: 8,
    can_skip: false
  }
];

export const LANGUAGE_LEVEL_DESCRIPTIONS: Record<LanguageLevel, string> = {
  absolute_beginner: 'Complete beginner - no prior knowledge',
  beginner_low: 'Basic words and phrases',
  beginner_high: 'Simple conversations and basic grammar',
  intermediate_low: 'Everyday conversations with some complexity',
  intermediate_high: 'Complex topics and advanced grammar',
  advanced_low: 'Fluent in most situations',
  advanced_high: 'Near-native proficiency',
  native: 'Native speaker level'
};

export const LEARNING_PURPOSE_LABELS: Record<LearningPurpose, string> = {
  travel: 'Travel & Tourism',
  business: 'Business Communication',
  academic: 'Academic Study',
  personal_interest: 'Personal Interest',
  professional_development: 'Professional Development',
  family_heritage: 'Family Heritage',
  relocation: 'Moving to New Country',
  cultural_exploration: 'Cultural Exploration'
};

export const TIME_COMMITMENT_LABELS: Record<TimeCommitment, string> = {
  '15_min_daily': '15 minutes per day',
  '30_min_daily': '30 minutes per day',
  '1_hour_daily': '1 hour per day',
  '2_hours_daily': '2+ hours per day',
  'weekend_only': 'Weekends only',
  'flexible': 'Flexible schedule'
};

export const FLUENCY_TIMELINE_LABELS: Record<FluencyTimeline, string> = {
  '3_months': '3 months',
  '6_months': '6 months',
  '1_year': '1 year',
  '2_years': '2 years',
  'no_rush': 'No specific timeline'
};

// Utility functions
export function calculateAssessmentLevel(score: number, totalQuestions: number): LanguageLevel {
  const accuracy = score / totalQuestions;

  if (accuracy >= 0.95) return 'advanced_high';
  if (accuracy >= 0.85) return 'advanced_low';
  if (accuracy >= 0.75) return 'intermediate_high';
  if (accuracy >= 0.65) return 'intermediate_low';
  if (accuracy >= 0.50) return 'beginner_high';
  if (accuracy >= 0.30) return 'beginner_low';
  return 'absolute_beginner';
}

export function getNextStep(currentStep: number, completedSteps: number[]): number {
  const nextStep = currentStep + 1;
  return nextStep;
}

export function canSkipStep(stepId: number): boolean {
  const step = ONBOARDING_STEPS.find(s => s.id === stepId);
  return step?.can_skip ?? false;
}

export function isStepRequired(stepId: number): boolean {
  const step = ONBOARDING_STEPS.find(s => s.id === stepId);
  return step?.required ?? true;
}

export function getTotalEstimatedTime(): number {
  return ONBOARDING_STEPS.reduce((total, step) => total + step.estimated_time, 0);
}